import type { Permission, RoleTemplate } from "@sitescene/constant";
import type { UserStatus } from "@sitescene/types";

import posthog from "@/lib/posthog";

export type InviteAcceptedRedirectTarget = "account_activation" | "dashboard";

export type UserProfileUpdateSource =
	| "account_activation"
	| "manage_profile"
	| "user_edit";

/**
 * Capture when an invitee opens the accept-invite link while signed in as the
 * invited email. Fires once per page load before redirecting to activation or
 * the dashboard.
 */
export function captureInviteAccepted({
	redirectTarget,
	userId,
	userStatus,
}: {
	redirectTarget: InviteAcceptedRedirectTarget;
	userId: string;
	userStatus: UserStatus;
}) {
	posthog.capture("invite_accepted", {
		redirect_target: redirectTarget,
		user_id: userId,
		user_status: userStatus,
	});
}

/**
 * Capture a successful invite. `role` is the template selected in the invite
 * UI and is omitted when the form is submitted without one.
 */
export function captureUserInvited({
	locations,
	permissions,
	role,
	userId,
}: {
	locations: string[];
	permissions: Permission[];
	role?: RoleTemplate;
	userId: string;
}) {
	posthog.capture("user_invited", {
		locations,
		permissions,
		...(role && { role }),
		user_id: userId,
	});
}

/**
 * Capture a successful profile save. `is_self` is derived from `source`.
 */
export function captureUserProfileUpdated({
	changedFields,
	source,
	userId,
}: {
	changedFields: string[];
	source: UserProfileUpdateSource;
	userId: string;
}) {
	posthog.capture("user_profile_updated", {
		changed_fields: changedFields,
		is_self: source !== "user_edit",
		source,
		user_id: userId,
	});
}
